'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { cn } from '@/app/utils';
import { ChevronDown2 } from '../../icons/ChevronDown2';
import type { FAQSectionData } from '../../types';
import { PortableTextContent } from '../PortableTextContent';
import { EASE_OUT_CUBIC, SectionShell } from '../section-shared';

export function FAQSection({ section }: { section: FAQSectionData }) {
  const [openIndex, setOpenIndex] = useState(0);
  const items = section.items ?? [];

  return (
    <SectionShell>
      <div className="flex gap-0 sm:flex-col sm:gap-8">
        <div className="w-[350px] shrink-0 sm:w-full">
          <h2 className="font-semibold text-[40px] text-white leading-[1.2] sm:text-[28px]">
            Frequently Asked
            <br />
            Questions
          </h2>
        </div>

        <div className="relative flex-1 pl-10 sm:pl-0">
          <div className="divide-y divide-white/10 border-white/10 border-y">
            {items?.map((item, index) => {
              const isOpen = openIndex === index;

              return (
                <div key={item._key || item.question}>
                  <button
                    className={cn(
                      'flex w-full cursor-pointer items-center justify-between gap-6 px-8 py-[30px] text-left sm:px-4 sm:py-5',
                      isOpen && 'pb-6',
                    )}
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    type="button"
                  >
                    <span className="flex-1 font-medium text-white text-xl leading-7 sm:text-base">
                      {item.question}
                    </span>
                    <ChevronDown2
                      className={cn(
                        'size-4 shrink-0 text-white transition-transform duration-300',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        animate={{ height: 'auto', opacity: 1 }}
                        className="overflow-hidden"
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: EASE_OUT_CUBIC }}
                      >
                        <div className="px-8 pb-8 sm:px-4 sm:pb-5">
                          <PortableTextContent value={item.answer} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
